import type { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Download, Linkedin } from 'lucide-react';
import {
  linkedInAddToProfileUrl,
  linkedInShareUrl,
  useApi,
  type Certificate,
  type CertificateFileFormat,
} from '@/lib/api';
import { Button, ErrorText } from './ui';

/** Downloads the signed-in learner's certificate (rendered on demand), public or private. */
export function CertificateDownloadButton({
  code,
  format = 'pdf',
  variant = 'secondary',
  className = '',
}: {
  code: string;
  format?: CertificateFileFormat;
  variant?: 'primary' | 'secondary' | 'ghost';
  className?: string;
}) {
  const api = useApi();
  const download = useMutation({ mutationFn: () => api.downloadCertificate(code, format) });
  return (
    <div className={className}>
      <Button
        variant={variant}
        className="w-full"
        disabled={download.isPending}
        onClick={() => download.mutate()}
      >
        <Download className="size-4" aria-hidden />
        {download.isPending ? 'Preparing…' : `Download ${format.toUpperCase()}`}
      </Button>
      <ErrorText error={download.error} />
    </div>
  );
}

const linkClass = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
  secondary: 'border border-border bg-card text-foreground hover:bg-muted',
};

function ExternalLink({
  href,
  variant,
  children,
}: {
  href: string;
  variant: keyof typeof linkClass;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 rounded-[var(--radius)] px-4 py-2 text-sm font-medium transition-colors ${linkClass[variant]}`}
    >
      {children}
    </a>
  );
}

/**
 * The owner's LinkedIn actions for a public certificate: post it (LinkedIn
 * shows the preview image from the verify page), or add it to their profile.
 */
export function LinkedInActions({
  cert,
}: {
  cert: Pick<Certificate, 'code' | 'title' | 'issuedAt' | 'timeZone'>;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <ExternalLink href={linkedInShareUrl(cert.code)} variant="primary">
        <Linkedin className="size-4" aria-hidden /> Share on LinkedIn
      </ExternalLink>
      <ExternalLink href={linkedInAddToProfileUrl(cert)} variant="secondary">
        <Linkedin className="size-4" aria-hidden /> Add Certificate to LinkedIn Profile
      </ExternalLink>
    </div>
  );
}
